import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  RotateCcw,
  Edit3,
  Lightbulb,
  Pause,
  Play,
  Loader2,
  Undo,
  Redo,
  ChevronDown,
} from "lucide-react";
import { SudokuApiService } from "@/services/sudokuApiService";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useIsDarkMode } from "@/hooks/useIsDarkMode";

type Difficulty = "Easy" | "Medium" | "Hard";

interface SudokuCell {
  value: number;
  isFixed: boolean;
  isSelected: boolean;
  notes: number[];
  isHighlighted: boolean;
  isError: boolean;
  isSameNumber: boolean;
  isConflicting: boolean;
}

interface GameState {
  board: SudokuCell[][];
  solution: number[][];
  difficulty: Difficulty;
  mistakes: number;
  time: number;
  // Timer should only start after first cell is filled
  hasStarted: boolean;
  isComplete: boolean;
  isPaused: boolean;
  notesMode: boolean;
  selectedCell: { row: number; col: number } | null;
  isLoading: boolean;
  history: SudokuCell[][][];
  historyIndex: number;
  // Track used hints to enforce per-difficulty limits
  hintsUsed: number;
}

const DIFFICULTIES: { [key in Difficulty]: number } = {
  Easy: 45,
  Medium: 35,
  Hard: 28,
};

export const SudokuGame: React.FC = () => {
  const isDarkMode = useIsDarkMode();
  const [showNewGameDialog, setShowNewGameDialog] = useState(false);
  const [pendingDifficulty, setPendingDifficulty] = useState<Difficulty | null>(
    null
  );
  // Custom dropdown state for desktop difficulty selector
  const [showDiffMenu, setShowDiffMenu] = useState(false);

  const [gameState, setGameState] = useState<GameState>({
    board: [],
    solution: [],
    difficulty: "Medium",
    mistakes: 0,
    time: 0,
    hasStarted: false,
    isComplete: false,
    isPaused: false,
    notesMode: false,
    selectedCell: null,
    isLoading: false,
    history: [],
    historyIndex: -1,
    hintsUsed: 0,
  });
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (gameState.hasStarted && !gameState.isPaused && !gameState.isComplete) {
      interval = setInterval(() => {
        setGameState((prev) => ({ ...prev, time: prev.time + 1 }));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [gameState.hasStarted, gameState.isPaused, gameState.isComplete]);

  // Hint limits per difficulty
  const getHintLimit = (difficulty: Difficulty): number => {
    switch (difficulty) {
      case "Easy":
        return 3;
      case "Medium":
        return 3;
      case "Hard":
        return 2;
      default:
        return 3;
    }
  };

  // Create puzzle board from API data
  const createPuzzleFromApi = (
    puzzleData: number[][],
    solutionData: number[][]
  ): SudokuCell[][] => {
    return puzzleData.map((row, rowIndex) =>
      row.map((value, colIndex) => ({
        value,
        isFixed: value !== 0,
        isSelected: false,
        notes: [],
        isHighlighted: false,
        isError: false,
        isSameNumber: false,
        isConflicting: false,
      }))
    );
  };

  // Initialize new game using API
  const saveGameState = useCallback((state: GameState) => {
    try {
      localStorage.setItem("sudokuGameState", JSON.stringify(state));
    } catch (error) {
      console.error("Error saving game state:", error);
    }
  }, []);

  const reconstructBoard = useCallback((boardData: any[][]): SudokuCell[][] => {
    return boardData.map((row) =>
      row.map((cell) => ({
        value: typeof cell.value === "number" ? cell.value : 0,
        isFixed: Boolean(cell.isFixed),
        isSelected: false,
        notes: Array.isArray(cell.notes) ? cell.notes : [],
        isHighlighted: false,
        isError: false,
        isSameNumber: false,
        isConflicting: false,
      }))
    );
  }, []);

  const newGame = async (difficulty?: Difficulty) => {
    const targetDifficulty = difficulty ?? gameState.difficulty;
    setGameState((prev) => ({ ...prev, isLoading: true }));

    try {
      const puzzleData = await SudokuApiService.fetchPuzzle(targetDifficulty);
      const board = createPuzzleFromApi(puzzleData.puzzle, puzzleData.solution);

      const newState: GameState = {
        board,
        solution: puzzleData.solution,
        difficulty: targetDifficulty,
        mistakes: 0,
        time: 0,
        hasStarted: false,
        isComplete: false,
        isPaused: false,
        notesMode: false,
        selectedCell: null,
        isLoading: false,
        history: [board.map((row) => row.map((cell) => ({ ...cell })))],
        historyIndex: 0,
        hintsUsed: 0,
      };

      setGameState(newState);
      saveGameState(newState);
    } catch (error) {
      console.error("Error loading new game:", error);
      setGameState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  // Load saved game or start a new one on entry
  useEffect(() => {
    const saved = loadGameState();
    if (saved) {
      const restoredBoard = updateAllErrors(reconstructBoard(saved.board));
      const restoredHistory = Array.isArray(saved.history)
        ? saved.history.map((entry: any[][]) =>
            updateAllErrors(reconstructBoard(entry))
          )
        : [restoredBoard.map((row) => row.map((cell) => ({ ...cell })))];

      setGameState((prev) => ({
        ...prev,
        ...saved,
        board: restoredBoard,
        history: restoredHistory,
        historyIndex:
          typeof saved.historyIndex === "number" &&
          saved.historyIndex < restoredHistory.length
            ? saved.historyIndex
            : restoredHistory.length - 1,
        isLoading: false,
        selectedCell: null,
      }));
    } else {
      newGame("Medium");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (gameState.board.length === 0 || gameState.isLoading) return;
    saveGameState(gameState);
  }, [gameState, saveGameState]);

  // Handle keyboard input for desktop users
  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      // Only handle keyboard input if a cell is selected and game is not paused/complete
      if (
        !gameState.selectedCell ||
        gameState.isPaused ||
        gameState.isComplete ||
        gameState.isLoading
      ) {
        return;
      }

      const { row, col } = gameState.selectedCell;
      const cell = gameState.board[row][col];

      // Don't allow input on fixed cells
      if (cell.isFixed) return;

      // Handle number keys 1-9
      const num = parseInt(event.key);
      if (num >= 1 && num <= 9) {
        event.preventDefault(); // Prevent default browser behavior
        handleNumberInput(num);
      }
      // Handle delete/backspace to clear cell
      else if (event.key === "Delete" || event.key === "Backspace") {
        event.preventDefault();
        handleNumberInput(0); // Clear the cell
      }
      // Handle space to toggle notes mode
      else if (event.key === " ") {
        event.preventDefault();
        toggleNotesMode();
      }
    },
    [
      gameState.selectedCell,
      gameState.isPaused,
      gameState.isComplete,
      gameState.isLoading,
    ]
  );

  // Add keyboard event listener
  useEffect(() => {
    document.addEventListener("keydown", handleKeyPress);
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleKeyPress]);

  // Handle cell click with conflict checking (optimized for performance)
  const handleCellClick = (row: number, col: number) => {
    if (gameState.isComplete) return;

    setGameState((prev) => {
      const clickedCell = prev.board[row][col];
      const clickedValue = clickedCell.value;
      const currentBoard = prev.board.map((row) => row.map((cell) => cell.value));
      
      // Get conflicting cells for the clicked cell if it has an error
      const clickedHasError = clickedValue > 0 ? hasConflict(currentBoard, row, col, clickedValue) : false;
      const conflictingCells = clickedHasError ? getConflictingCells(currentBoard, row, col, clickedValue) : [];
      const conflictKeySet = new Set(conflictingCells.map(({ row, col }) => `${row}-${col}`));
      
      const newBoard = prev.board.map((boardRow, r) =>
        boardRow.map((cell, c) => {
          if (r === row && c === col) {
            // Update highlighting for the selected cell
            return {
              ...cell,
              isSelected: true,
              isHighlighted: false, // Selected cell shouldn't be highlighted
              isSameNumber: false, // Selected cell shouldn't show same number highlight
              isConflicting: false, // Selected cell shouldn't show conflicting highlight
              isError: clickedHasError,
            };
          } else {
            // Update highlighting for other cells
            const shouldBeHighlighted =
              r === row ||
              c === col ||
              (Math.floor(r / 3) === Math.floor(row / 3) &&
                Math.floor(c / 3) === Math.floor(col / 3));
            
            // Check if this cell has the same number as clicked cell
            const isSameNumber = clickedValue > 0 && cell.value === clickedValue;
            
            // Check if this cell is conflicting with the selected cell
            const isConflicting = conflictKeySet.has(`${r}-${c}`);

            return {
              ...cell,
              isSelected: false,
              isHighlighted: shouldBeHighlighted,
              isSameNumber: isSameNumber,
              isConflicting: isConflicting,
              // Check if this cell has a conflict (only recalculate if needed)
              isError:
                cell.value > 0
                  ? hasConflict(currentBoard, r, c, cell.value)
                  : false,
            };
          }
        })
      );

      return {
        ...prev,
        board: newBoard,
        selectedCell: { row, col },
      };
    });
  };

  // Handle number input (optimized for performance)
  const handleNumberInput = (num: number) => {
    if (!gameState.selectedCell || gameState.isComplete) return;

    const { row, col } = gameState.selectedCell;
    const cell = gameState.board[row][col];

    if (cell.isFixed) return;

    setGameState((prev) => {
      if (prev.notesMode) {
        // Toggle note (optimized shallow copy)
        const notes = [...cell.notes];
        const noteIndex = notes.indexOf(num);
        if (noteIndex > -1) {
          notes.splice(noteIndex, 1);
        } else {
          notes.push(num);
          notes.sort();
        }
        const newBoard = prev.board.map((boardRow, r) =>
          r === row
            ? boardRow.map((boardCell, c) =>
                c === col ? { ...boardCell, notes, value: 0 } : boardCell
              )
            : boardRow
        );
        return { ...prev, board: newBoard };
      } else {
        // Update value and recalculate conflicts (optimized)
        const currentBoardValues = prev.board.map((boardRow) =>
          boardRow.map((boardCell) => boardCell.value)
        );

        currentBoardValues[row][col] = num;

        const selectedHasConflict =
          num > 0 ? hasConflict(currentBoardValues, row, col, num) : false;

        const conflictingCells = selectedHasConflict
          ? getConflictingCells(currentBoardValues, row, col, num)
          : [];

        const conflictKeySet = new Set(
          conflictingCells.map(({ row, col }) => `${row}-${col}`)
        );

        // Only update cells that need updating
        const updatedBoard = prev.board.map((boardRow, r) =>
          boardRow.map((boardCell, c) => {
            const isSelectedCell = r === row && c === col;
            if (isSelectedCell) {
              return {
                ...boardCell,
                value: num,
                notes: [],
                isError: selectedHasConflict,
                isConflicting: false,
              };
            }
            
            // Only recalculate conflicts for cells that might be affected
            const needsUpdate = conflictKeySet.has(`${r}-${c}`) || 
                               (boardCell.value > 0 && (r === row || c === col || 
                               (Math.floor(r / 3) === Math.floor(row / 3) && Math.floor(c / 3) === Math.floor(col / 3))));
            
            if (needsUpdate) {
              const cellHasConflict = boardCell.value > 0
                ? hasConflict(currentBoardValues, r, c, boardCell.value)
                : false;
              
              return {
                ...boardCell,
                isError: cellHasConflict,
                isConflicting: conflictKeySet.has(`${r}-${c}`),
              };
            }
            
            return boardCell;
          })
        );

        // Track history with the updated board
        addToHistory(updatedBoard);

        // Check if puzzle is complete using the updated values
        const isComplete =
          SudokuApiService.isPuzzleComplete(currentBoardValues) &&
          updatedBoard.every((boardRow) =>
            boardRow.every(
              (boardCell) =>
                boardCell.isFixed ||
                (boardCell.value > 0 && !boardCell.isError)
            )
          );

        return {
          ...prev,
          board: updatedBoard,
          mistakes:
            selectedHasConflict && num > 0
              ? prev.mistakes + 1
              : prev.mistakes,
          isComplete,
          hasStarted: prev.hasStarted || num > 0,
        };
      }
    });
  };

  // Format time
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  // Toggle pause
  const togglePause = () => {
    setGameState((prev) => ({ ...prev, isPaused: !prev.isPaused }));
  };

  // Handle new game with confirmation
  const handleNewGame = () => {
    const targetDifficulty = pendingDifficulty ?? gameState.difficulty;
    setShowNewGameDialog(false);
    setPendingDifficulty(null);
    newGame(targetDifficulty);
  };

  // Show confirmation dialog only if game has started
  const showNewGameConfirmation = (difficulty?: Difficulty) => {
    const targetDifficulty = difficulty ?? gameState.difficulty;
    if (gameState.hasStarted && !gameState.isComplete) {
      setPendingDifficulty(targetDifficulty);
      setShowNewGameDialog(true);
    } else {
      newGame(targetDifficulty);
    }
  };

  // Toggle notes mode
  const toggleNotesMode = () => {
    setGameState((prev) => ({ ...prev, notesMode: !prev.notesMode }));
  };

  // Clear saved game state
  const clearSavedGame = () => {
    try {
      localStorage.removeItem("sudokuGameState");
    } catch (error) {
      console.error("Error clearing saved game:", error);
    }
  };

  // Load game state from localStorage
  const loadGameState = (): GameState | null => {
    try {
      const saved = localStorage.getItem("sudokuGameState");
      if (saved) {
        const parsedState = JSON.parse(saved);
        // Ensure board is properly reconstructed
        if (parsedState.board && parsedState.board.length > 0) {
          return {
            ...parsedState,
            board: parsedState.board.map((row: any[]) =>
              row.map((cell: any) => ({
                value: cell.value || 0,
                isFixed: cell.isFixed || false,
                isSelected: cell.isSelected || false,
                notes: cell.notes || [],
                isHighlighted: cell.isHighlighted || false,
                isError: cell.isError || false,
                isSameNumber: cell.isSameNumber || false,
                isConflicting: cell.isConflicting || false,
              }))
            ),
          };
        }
      }
    } catch (error) {
      console.error("Error loading game state:", error);
    }
    return null;
  };

  // Check for conflicts in row, column, and 3x3 box
  const hasConflict = (
    board: number[][],
    row: number,
    col: number,
    num: number
  ): boolean => {
    // Check row for duplicates
    for (let c = 0; c < 9; c++) {
      if (c !== col && board[row][c] === num) {
        return true;
      }
    }

    // Check column for duplicates
    for (let r = 0; r < 9; r++) {
      if (r !== row && board[r][col] === num) {
        return true;
      }
    }

    // Check 3x3 box for duplicates
    const boxStartRow = Math.floor(row / 3) * 3;
    const boxStartCol = Math.floor(col / 3) * 3;
    for (let r = boxStartRow; r < boxStartRow + 3; r++) {
      for (let c = boxStartCol; c < boxStartCol + 3; c++) {
        if ((r !== row || c !== col) && board[r][c] === num) {
          return true;
        }
      }
    }

    return false;
  };

  // Find all conflicting cells for a given cell
  const getConflictingCells = (
    board: number[][],
    row: number,
    col: number,
    num: number
  ): { row: number; col: number }[] => {
    const conflicts: { row: number; col: number }[] = [];
    
    if (num === 0) return conflicts;

    // Check row for duplicates
    for (let c = 0; c < 9; c++) {
      if (c !== col && board[row][c] === num) {
        conflicts.push({ row, col: c });
      }
    }

    // Check column for duplicates
    for (let r = 0; r < 9; r++) {
      if (r !== row && board[r][col] === num) {
        conflicts.push({ row: r, col });
      }
    }

    // Check 3x3 box for duplicates
    const boxStartRow = Math.floor(row / 3) * 3;
    const boxStartCol = Math.floor(col / 3) * 3;
    for (let r = boxStartRow; r < boxStartRow + 3; r++) {
      for (let c = boxStartCol; c < boxStartCol + 3; c++) {
        if ((r !== row || c !== col) && board[r][c] === num) {
          conflicts.push({ row: r, col: c });
        }
      }
    }

    return conflicts;
  };

  // Clear errors when deselecting cells
  const clearAllErrors = (board: SudokuCell[][]) => {
    return board.map((row) =>
      row.map((cell) => ({
        ...cell,
        isError: false,
        isSameNumber: false,
        isConflicting: false,
      }))
    );
  };

  // Update all error states based on current board
  const updateAllErrors = (board: SudokuCell[][]) => {
    const currentBoard = board.map((row) => row.map((cell) => cell.value));
    return board.map((row, r) =>
      row.map((cell, c) => ({
        ...cell,
        isError:
          cell.value > 0 ? hasConflict(currentBoard, r, c, cell.value) : false,
        isSameNumber: false,
        isConflicting: false,
      }))
    );
  };

  // Add to history
  const addToHistory = (board: SudokuCell[][]) => {
    setGameState((prev) => {
      const newHistory = prev.history.slice(0, prev.historyIndex + 1);
      newHistory.push(board.map((row) => row.map((cell) => ({ ...cell }))));
      const newState = {
        ...prev,
        history: newHistory,
        historyIndex: newHistory.length - 1,
      };
      // Save to localStorage
      try {
        localStorage.setItem("sudokuGameState", JSON.stringify(newState));
      } catch (error) {
        console.error("Error saving game state:", error);
      }

      return newState;
    });
  };

  // Hint function - reveal the correct number for selected cell
  const useHint = () => {
    // Enforce hint limits per difficulty
    if (gameState.hintsUsed >= getHintLimit(gameState.difficulty)) return;
    if (!gameState.selectedCell || gameState.isComplete) return;

    const { row, col } = gameState.selectedCell;
    const cell = gameState.board[row][col];

    if (cell.isFixed) return; // Can't use hint on fixed cells

    const correctNumber = gameState.solution[row][col];
    const currentBoard = gameState.board.map((row) =>
      row.map((cell) => cell.value)
    );

    // Check if placing the correct number would cause a conflict
    if (hasConflict(currentBoard, row, col, correctNumber)) {
      // If it would cause a conflict, clear the cell instead
      const newBoard = [...gameState.board];
      newBoard[row][col] = {
        ...cell,
        value: 0,
        notes: [],
        isError: false,
        isSameNumber: false,
        isConflicting: false,
      };

      addToHistory(newBoard);

      setGameState((prev) => ({
        ...prev,
        board: newBoard,
        mistakes: prev.mistakes + 1, // Using hint counts as a mistake
        selectedCell: null,
        hintsUsed: prev.hintsUsed + 1,
      }));
    } else {
      // Place the correct number
      const newBoard = [...gameState.board];
      newBoard[row][col] = {
        ...cell,
        value: correctNumber,
        notes: [],
        isError: false,
        isSameNumber: false,
        isConflicting: false,
      };

      addToHistory(newBoard);

      setGameState((prev) => ({
        ...prev,
        board: newBoard,
        selectedCell: null,
        hintsUsed: prev.hintsUsed + 1,
        hasStarted: prev.hasStarted || true,
      }));
    }
  };

  // Undo function
  const undo = () => {
    setGameState((prev) => {
      if (prev.historyIndex > 0) {
        const newIndex = prev.historyIndex - 1;
        const boardFromHistory = prev.history[newIndex].map((row) =>
          row.map((cell) => ({ ...cell }))
        );
        return {
          ...prev,
          board: boardFromHistory,
          historyIndex: newIndex,
          selectedCell: null,
        };
      }
      return prev;
    });
  };

  // Redo function
  const redo = () => {
    setGameState((prev) => {
      if (prev.historyIndex < prev.history.length - 1) {
        const newIndex = prev.historyIndex + 1;
        const boardFromHistory = prev.history[newIndex].map((row) =>
          row.map((cell) => ({ ...cell }))
        );
        return {
          ...prev,
          board: boardFromHistory,
          historyIndex: newIndex,
          selectedCell: null,
        };
      }
      return prev;
    });
  };

  return (
    <div className="w-full max-w-4xl mx-auto sm:p-4 p-0 space-y-6">
      <div className="flex row sm:justify-between justify-center w-full gap-6 px-2">
        {/* Desktop Difficulty Selector - Custom Dropdown */}
        <div className="flex relative">
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              className="text-xs  rounded-full px-0 sm:w-[120px] w-[100px]"
              onClick={() => setShowDiffMenu((v) => !v)}
              disabled={gameState.isLoading}
            >
              <span>{gameState.difficulty}</span>
              <span className="ml-2">
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${
                    showDiffMenu ? "rotate-180" : "rotate-0"
                  }`}
                />
              </span>
            </Button>
            {showDiffMenu && (
              <div
                className="absolute mt-2 w-40 bg-white text-gray-900 rounded-md shadow-lg ring-1 ring-black/5 z-20 overflow-hidden"
                onMouseLeave={() => setShowDiffMenu(false)}
              >
                {Object.keys(DIFFICULTIES).map((diff) => (
                  <button
                    key={diff}
                    onClick={() => {
                      setShowDiffMenu(false);
                      showNewGameConfirmation(diff as Difficulty);
                    }}
                    disabled={gameState.isLoading}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
                      gameState.difficulty === diff
                        ? "bg-sky-100 font-semibold"
                        : ""
                    }`}
                  >
                    {diff}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        {/* Desktop Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-4">
            <div
              className={`text-sm ${
                isDarkMode ? "text-gray-300" : "text-slate-600"
              }`}
            >
              Mistakes:{" "}
              <span className={isDarkMode ? "text-red-400" : "text-red-500"}>
                {gameState.mistakes}
              </span>
            </div>
            <div
              className={`text-sm ${
                isDarkMode ? "text-gray-300" : "text-slate-600"
              }`}
            >
              Time:{" "}
              <span className={isDarkMode ? "text-blue-400" : "text-blue-500"}>
                {formatTime(gameState.time)}
              </span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={togglePause}
              className="h-8 w-8"
            >
              {gameState.isPaused ? (
                <Play className="w-4 h-4" />
              ) : (
                <Pause className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="flex column sm:row w-full gap-6">
        <div className="flex w-full flex-col gap-4 justify-center items-center">
          {/* Game Board */}
          <div className="flex column w-full">
            <div className="relative w-full flex items-center sm:justify-start justify-center">
              <div className="grid grid-cols-9 gap-0 bg-gray-100 p-2 rounded-lg w-full max-w-sm sm:max-w-md border border-gray-300 relative sudoku-grid">
              {gameState.isPaused && !gameState.isLoading && (
                <div className="absolute inset-0 bg-transparent backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
                  <div className="text-center">
                    <Pause className="w-12 h-12 mx-auto mb-2 text-[#1c1c1c]" />
                    <p className="text-[#1c1c1c] text-lg">Game Paused</p>
                  </div>
                </div>
              )}
              {gameState.isLoading && (
                <div className="absolute inset-0 bg-transparent backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
                  <div className="text-center">
                    <Loader2 className="w-12 h-12 mx-auto mb-2 text-[#1c1c1c] animate-spin" />
                    <p className="text-[#1c1c1c] text-lg">
                      Creating New Sudoku...
                    </p>
                  </div>
                </div>
              )}
                {gameState.board.map((row, rowIndex) =>
                  row.map((cell, colIndex) => (
                    <button
                      key={`${rowIndex}-${colIndex}`}
                      onClick={() => handleCellClick(rowIndex, colIndex)}
                      className={`
                    aspect-square flex items-center select-none justify-center font-normal
                    relative text-xl sm:text-xl transition-colors duration-150
                    border-t border-l border-gray-300 dark:border-gray-600
                    ${
                      // Add right border on last column
                      (colIndex === 8 ? ' border-r' : '') +
                      // Add bottom border on last row
                      (rowIndex === 8 ? ' border-b' : '') +
                      // Thicker borders for 3x3 separators (right and bottom)
                      (((colIndex + 1) % 3 === 0) ? ' border-r-2' : '') +
                      (((rowIndex + 1) % 3 === 0) ? ' border-b-2' : '')
                    }
                    ${
                      cell.isError
                        ? " text-gray-900 font-bold"
                        : cell.isSelected
                        ? " border-blue-400 text-gray-900"
                        : cell.isConflicting
                        ? " text-gray-900 font-semibold"
                        : cell.isSameNumber
                        ? " text-gray-900 font-bold"
                        : cell.isHighlighted
                        ? " text-gray-900"
                        : cell.isFixed
                        ? "bg-white text-gray-800"
                        : "bg-white text-gray-900 hover:bg-gray-200"
                    }
                  `}
                      style={{
                        backgroundColor: cell.isError
                          ? "#dc262660" // Dark red for the cell with error
                          : cell.isSelected
                          ? " #7bc3ff90"
                          : cell.isConflicting
                          ? "#fca5a5" // Light red for conflicting cells
                          : cell.isSameNumber
                          ? "#ffd70050"
                          : cell.isHighlighted
                          ? "#4f81ff10"
                          : !cell.isFixed && cell.value > 0
                          ? "#bcdfff40"
                          : undefined,
                      }}
                    >
                      {cell.value > 0 ? (
                        cell.value
                      ) : (
                        <div className="grid grid-cols-3 gap-0 text-[6px] sm:text-xs text-gray-500 w-full h-full p-[0px]">
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((note) => (
                            <div
                              key={note}
                              className="flex items-center justify-center h-full text-center leading-none"
                            >
                              {cell.notes.includes(note) ? note : ""}
                            </div>
                          ))}
                        </div>
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Mobile Controls */}
          <div className="block sm:hidden space-y-4">
            {/* Action Buttons */}
            <div className="flex gap-2 justify-center">
              <div className="relative group">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={useHint}
                  disabled={
                    !gameState.selectedCell ||
                    gameState.isComplete ||
                    gameState.hintsUsed >= getHintLimit(gameState.difficulty)
                  }
                  className="relative rounded-full"
                >
                  <Lightbulb className="w-5 h-5 font-bold" strokeWidth={2.5} />
                  <span
                    className={`absolute -top-1 -right-1 text-md font-bold px-1 rounded-full w-[20px]  ${
                      gameState.hintsUsed >= getHintLimit(gameState.difficulty)
                        ? "text-gray-600 bg-gray-100"
                        : "text-blue-600 bg-blue-100"
                    }`}
                  >
                    {Math.max(
                      0,
                      getHintLimit(gameState.difficulty) - gameState.hintsUsed
                    )}
                  </span>
                </Button>
                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  Hint
                </div>
              </div>

              <div className="relative group">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={undo}
                  disabled={gameState.historyIndex <= 0}
                  className="relative rounded-full"
                >
                  <Undo className="w-5 h-5 font-bold" strokeWidth={2.5} />
                </Button>
                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs rounded-full bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  Undo
                </div>
              </div>

              <div className="relative group">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={redo}
                  disabled={
                    gameState.historyIndex >= gameState.history.length - 1
                  }
                  className="relative rounded-full"
                >
                  <Redo className="w-5 h-5 font-bold" strokeWidth={2.5} />
                </Button>
                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs rounded-full bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  Redo
                </div>
              </div>

              <div className="relative group">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={toggleNotesMode}
                  className="relative rounded-full"
                >
                  <Edit3 className="w-5 h-5 font-bold" strokeWidth={2.5} />
                  <span
                    className={`absolute -top-1 -right-1 text-[8px] font-bold px-1 rounded ${
                      gameState.notesMode
                        ? "text-blue-600 bg-blue-100"
                        : "text-gray-600 bg-gray-100"
                    }`}
                  >
                    {gameState.notesMode ? "ON" : "OFF"}
                  </span>
                </Button>
                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  Notes
                </div>
              </div>

              <div className="relative group">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => showNewGameConfirmation()}
                  disabled={gameState.isLoading}
                  className="rounded-full"
                >
                  {gameState.isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RotateCcw className="w-4 h-4" />
                  )}
                </Button>
                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  New game
                </div>
              </div>
            </div>

            {/* Number Pad */}
            <div className="grid grid-cols-9 gap-1 max-w-sm mx-auto">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                <Button
                  key={num}
                  variant="outline"
                  onClick={() => handleNumberInput(num)}
                  className="text-lg font-normal bg-transparent text-white border-0"
                  disabled={gameState.isLoading}
                >
                  {num}
                </Button>
              ))}
            </div>

            {/* New Game Button */}
            <div className="flex justify-center">
              <Button
                onClick={() => showNewGameConfirmation()}
                className="w-[120px] bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3"
                disabled={gameState.isLoading}
              >
                {gameState.isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  "New Game"
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Desktop Controls */}
        <div className="hidden sm:flex flex-row gap-6 items-start">
          {/* Game Board is already above */}

          {/* Controls */}
          <div className="flex flex-col space-y-4">

            {/* Action Buttons */}
            <div className="flex gap-2">
              <div className="relative group">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={useHint}
                  disabled={
                    !gameState.selectedCell ||
                    gameState.isComplete ||
                    gameState.hintsUsed >= getHintLimit(gameState.difficulty)
                  }
                  className="relative rounded-full"
                >
                  <Lightbulb className="w-5 h-5 font-bold" strokeWidth={2.5} />
                  <span
                    className={`absolute -top-1 -right-1 text-md font-bold px-1 rounded-full w-[20px]  ${
                      gameState.hintsUsed >= getHintLimit(gameState.difficulty)
                        ? "text-gray-600 bg-gray-100"
                        : "text-blue-600 bg-blue-100"
                    }`}
                  >
                    {Math.max(
                      0,
                      getHintLimit(gameState.difficulty) - gameState.hintsUsed
                    )}
                  </span>
                </Button>
                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  Hint
                </div>
              </div>

              <div className="relative group">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={undo}
                  disabled={gameState.historyIndex <= 0}
                  className="relative rounded-full"
                >
                  <Undo className="w-5 h-5 font-bold" strokeWidth={2.5} />
                </Button>
                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs rounded-full bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  Undo
                </div>
              </div>

              <div className="relative group">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={redo}
                  disabled={
                    gameState.historyIndex >= gameState.history.length - 1
                  }
                  className="relative rounded-full"
                >
                  <Redo className="w-5 h-5 font-bold" strokeWidth={2.5} />
                </Button>
                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs rounded-full bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  Redo
                </div>
              </div>

              <div className="relative group">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={toggleNotesMode}
                  className="relative rounded-full"
                >
                  <Edit3 className="w-5 h-5 font-bold" strokeWidth={2.5} />
                  <span
                    className={`absolute -top-1 -right-1 text-[8px] font-bold px-1 rounded ${
                      gameState.notesMode
                        ? "text-blue-600 bg-blue-100"
                        : "text-gray-600 bg-gray-100"
                    }`}
                  >
                    {gameState.notesMode ? "ON" : "OFF"}
                  </span>
                </Button>
                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  Notes
                </div>
              </div>

              <div className="relative group">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => showNewGameConfirmation()}
                  disabled={gameState.isLoading}
                  className="rounded-full"
                >
                  {gameState.isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RotateCcw className="w-4 h-4" />
                  )}
                </Button>
                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  New game
                </div>
              </div>
            </div>
            {/* Number Pad */}
            <div className="grid grid-cols-3 gap-2">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                <Button
                  key={num}
                  variant="outline"
                  onClick={() => handleNumberInput(num)}
                  className=" text-lg font-thin"
                  disabled={gameState.isLoading}
                >
                  {num}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showNewGameDialog} onOpenChange={setShowNewGameDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start New Game</DialogTitle>
            <DialogDescription>
              Current game progress will be lost
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setPendingDifficulty(null);
                setShowNewGameDialog(false);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleNewGame}>OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Completion Message */}
      {gameState.isComplete && (
        <div className="text-center p-4 bg-green-500/20 rounded-lg border border-green-500/30">
          <h2 className="text-xl font-bold text-green-400 mb-2">
            Congratulations!
          </h2>
          <p className="text-gray-300">
            You completed the {gameState.difficulty} puzzle in{" "}
            {formatTime(gameState.time)} with {gameState.mistakes} mistakes!
          </p>
        </div>
      )}
    </div>
  );
};
